import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Alert, Badge, Button, Col, Input, Row, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import { fetchAccountingDashboard } from "../../../api/accounting";
import { formatDate, formatTZS } from "../../../utils/format";
import CustomizerContext from "../../../_helper/Customizer";
import TablePageMetricCard from "../Common/TablePageMetricCard";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runLoad, runRefresh } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import { EXPENSE_STATUS_META, JOURNAL_STATUS_META } from "./accountingUtils";

const AccountingOverviewPage = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const pub = process.env.PUBLIC_URL || "";
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const link = (path) => `${pub}/credora/${path}/${layoutURL}`;

  useEffect(() => {
    fetchBranches(true).then((rows) => {
      setBranches(rows || []);
      if (rows?.length) setBranchId(String(rows[0].id));
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(() => fetchAccountingDashboard(branchId || undefined).then(setData), {
      errorMessage: "Failed to load accounting dashboard.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load accounting dashboard.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const expenseColumns = useMemo(
    () => [
      { name: "Reference", selector: (r) => r.reference, width: "110px" },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      {
        name: "Amount",
        cell: (r) => formatTZS(r.amount),
        right: true,
        width: "130px",
      },
      {
        name: "Status",
        cell: (r) => {
          const meta = EXPENSE_STATUS_META[r.status] || { label: r.status, color: "secondary" };
          return <Badge color={meta.color}>{meta.label}</Badge>;
        },
        width: "110px",
      },
    ],
    []
  );

  const journalColumns = useMemo(
    () => [
      { name: "Number", selector: (r) => r.entry_number, width: "110px" },
      {
        name: "Date",
        cell: (r) => formatDate(r.entry_date),
        width: "110px",
      },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      {
        name: "Status",
        cell: (r) => {
          const meta = JOURNAL_STATUS_META[r.status] || { label: r.status, color: "secondary" };
          return <Badge color={meta.color}>{meta.label}</Badge>;
        },
        width: "100px",
      },
    ],
    []
  );

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Accounting Overview" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <Input type="select" bsSize="sm" style={{ width: "200px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </Input>
          <div className="d-flex flex-wrap gap-2">
            <Button color="light" size="sm" className="border" onClick={() => runRefresh(load, { label: "Accounting overview" })} disabled={loading}>
              Refresh
            </Button>
            <Link to={link("accounting/expenses")} className="btn btn-sm btn-outline-primary">Expenses</Link>
            <Link to={link("accounting/journal-entries")} className="btn btn-sm btn-outline-primary">Journals</Link>
            <Link to={link("accounting/settings")} className="btn btn-sm btn-outline-secondary">Settings</Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : data ? (
          <>
            <Row className="g-3 mb-3">
              <Col md="3" sm="6">
                <TablePageMetricCard title="Open periods" value={data.open_periods} subtitle={`${data.closed_periods} closed`} />
              </Col>
              <Col md="3" sm="6">
                <TablePageMetricCard title="Active accounts" value={data.active_accounts} subtitle="Chart of accounts" />
              </Col>
              <Col md="3" sm="6">
                <TablePageMetricCard title="Draft journals" value={data.draft_journals} subtitle={`${data.posted_journals_mtd} posted MTD`} />
              </Col>
              <Col md="3" sm="6">
                <TablePageMetricCard
                  title="Expenses MTD"
                  value={formatTZS(data.total_expenses_mtd)}
                  subtitle={`${data.pending_expenses} pending · ${data.approved_expenses} approved`}
                />
              </Col>
            </Row>

            <Row className="g-3">
              <Col lg="6">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Recent expenses</h6>
                    <Link to={link("accounting/expenses")}>View all</Link>
                  </div>
                  <div className="card-body p-0">
                    <DataTable
                      columns={expenseColumns}
                      data={data.recent_expenses || []}
                      customStyles={DEFAULT_TABLE_CUSTOM_STYLES}
                      dense
                      noHeader
                    />
                  </div>
                </div>
              </Col>
              <Col lg="6">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Recent journals</h6>
                    <Link to={link("accounting/journal-entries")}>View all</Link>
                  </div>
                  <div className="card-body p-0">
                    <DataTable
                      columns={journalColumns}
                      data={data.recent_journals || []}
                      customStyles={DEFAULT_TABLE_CUSTOM_STYLES}
                      dense
                      noHeader
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        ) : null}
      </div>
    </Fragment>
  );
};

export default AccountingOverviewPage;
